import { Effect, Exit, Option } from 'effect';

import { Cart } from '@vs-shop/engine/capability';
import { writeIntoCart } from '@vs-shop/engine/capability/cart/cartToken';
import { confirmCartWrite } from '@vs-shop/engine/module/cart/confirmCartWrite';
import { CartError } from '@vs-shop/engine/capability/cart/errors';
import type { CartWriteError } from '@vs-shop/engine/capability/cart/errors';
import type { Cart as CartView } from '@vs-shop/engine/capability/cart/cartSchema';
import { TenantConfig } from '../../service/tenant/TenantConfig';
import type { ProductForGTM } from '../../utils/gtm/create/cart-items-update';
import { createAddToCartPayloadFromProduct } from '../../utils/gtm/create/cart-items-update';
import { createSelectPromotionPayloadFromProduct } from '../../utils/gtm/create/select-promotion';
import { sendGtmEvent } from '../../utils/gtm/gtm';

export type AddCouponAndProductToOrderData = {
  couponCode: Option.Option<string>;
  quantity: number;
  product: ProductForGTM;
};

/** A successful command always confirms the product add. The coupon has its own outcome. */
type PromotionalCartAdd = {
  cart: CartView;
  coupon: Exit.Exit<CartView, CartWriteError>;
};

export default function addCouponAndProductToOrder({
  couponCode,
  quantity,
  product,
}: AddCouponAndProductToOrderData) {
  return Effect.gen(function* () {
    const { currency } = yield* TenantConfig;
    const code = yield* Option.match(couponCode, {
      onNone: () =>
        Effect.fail(
          new CartError.ContractMismatch({
            operation: 'applyCoupon',
            reference: Option.none(),
            cause: new Error('Promotion code is missing'),
          })
        ),
      onSome: Effect.succeed,
    });

    const cart = yield* writeIntoCart((token) =>
      Effect.gen(function* () {
        const service = yield* Cart;
        const before = yield* service.cart(token);
        const baseline = before.items.reduce(
          (sum, item) => (item.variantCode === product.code ? sum + item.quantity : sum),
          0
        );
        return yield* confirmCartWrite(
          token,
          service.addItem({ token, variantCode: product.code, quantity }),
          (current) =>
            current.items.reduce(
              (sum, item) => (item.variantCode === product.code ? sum + item.quantity : sum),
              0
            ) >=
            baseline + quantity
        );
      })
    );

    yield* createAddToCartPayloadFromProduct(product, quantity, currency).pipe(
      Effect.flatMap((event) => sendGtmEvent(event, true)),
      Effect.ignore
    );

    const coupon = yield* confirmCartWrite(
      cart.token,
      Cart.pipe(Effect.flatMap((service) => service.applyCoupon({ token: cart.token, code }))),
      (current) => Option.exists(current.coupon, (applied) => applied.code === code)
    ).pipe(Effect.exit);

    if (Exit.isSuccess(coupon)) {
      yield* createSelectPromotionPayloadFromProduct({ id: code }, product, quantity).pipe(
        Effect.flatMap((event) => sendGtmEvent(event, true)),
        Effect.ignore
      );
    }

    return { cart, coupon };
  });
}
